import {useId, useMemo} from "react";
import {useTransform} from "framer-motion";
import {VisuallyHidden} from "components/Accessibility.styles";
import {uploadSpeedDebugEnabled} from "core/debugFlags";
import formatBytes from "core/format/byteFormat";
import {formatPercent} from "core/format/percentFormat";
import {senderFileKind} from "download/fileKind";
import {Mode} from "home/gating";
import {GameOffer} from "games/GameOffer";
import {uploadGameProgress} from "games/gameProgress";
import {GameStage} from "games/GameStage";
import type {TransferGame} from "games/useTransferGame";
import {useLocale, useMessages} from "i18n";
import {useWindowedList} from "components/windowedList/useWindowedList";
import {useTheme} from "styled-components";
import {progressArcColor} from "home/personalization/personalizationAccent";
import type {Locale} from "i18n/localePreference";
import {AnimatedNumber} from "./AnimatedNumber";
import {announcementStep, type EtaWords, formatRate, percentOf, UploadProgress} from "./progressMath";
import type {ProgressReadoutValues} from "./progressReadout";
import {EASED_METER_SECONDS, EASED_VALUE_SECONDS, useEasedValue} from "./useEasedValue";
import type {ProgressReadoutSnapshot} from "./useProgressReadout";
import type {ConnectionState} from "core/upload/connectionState";
import {announcedNoteKey, uploadNoteKey} from "./uploadNote";
import {
	CancelButton,
	CompactGauge,
	CompactMeta,
	CompactPercent,
	FileInfo,
	FileName,
	FilePercent,
	FileRow,
	FileTop,
	Gauge,
	GaugeCenter,
	GaugeDetail,
	GaugePercent,
	GaugeProgressArc,
	GaugeSvg,
	GaugeTrack,
	GaugeVisual,
	GameOfferSlot,
	Head,
	IndeterminateFill,
	InstantSub,
	InstantTitle,
	MeterFill,
	MeterTrack,
	QUIET_LIST_ROW_GAP_PX,
	QUIET_LIST_ROW_HEIGHT_ESTIMATE_PX,
	QuietList,
	Stage,
	StageContent,
	ThumbSm,
	Title,
	UploadNote,
	UploadRoot,
} from "./ProgressCard.styles";

/**
 * v3 upload progress (ticket #20; smoothing + gauge in ui#942) — the LAST STATE of the create card's
 * payload box rather than a separate screen. The box keeps its frame and its place; inside it, one
 * large ring gives the percentage priority, with the time left and the bytes sent in small text under
 * the percentage, inside the ring (owner decision 2026-09-25, kitecorp/transferify-ui#2888; it was a
 * semicircular gauge with the sizes at its endpoints). Per-file rows remain as a quiet strip at the
 * bottom for multi-file transfers.
 *
 * Every changing gauge value is captured when {@link ./progressReadout} publishes through
 * {@link useProgressReadout}, about once a second. The arc and visible numbers then travel to that
 * shared snapshot via {@link ./useEasedValue}; reduced-motion readers receive each target instantly.
 *
 * Instant (P2P) mode uploads no bytes — registration returns in well under a second — so it keeps a
 * brief indeterminate state (its live view is the success phase's {@link InstantLiveCard}).
 */

interface Props {
	mode: Mode;
	files: File[];
	progress: UploadProgress;
	/**
	 * The smoothed rate/ETA sample, owned by {@link ../CreateFlow} since ui#1839.
	 *
	 * Handed down rather than measured here because the model has to outlive one mount of this card: its
	 * timer must be off for the landing-page visitors the flow also renders for, and its baseline must be
	 * forgotten between two transfers, and neither is something this card can see. `null` until the
	 * model's first window closes.
	 */
	readout: ProgressReadoutSnapshot | null;
	onCancel: () => void;
	/** Byte Runner's slot on this upload (kitecorp/transferify-ui#2872). */
	game: TransferGame;
	/**
	 * The upload finished while the sender was in the game, so the success card is waiting for them:
	 * the game runs to its finish flag and its button (or the ×) calls {@link onContinue}.
	 */
	completedWhilePlaying: boolean;
	onContinue: () => void;
	/**
	 * The colour the sender picked for this transfer, else their account's own, or null
	 * (`effectivePersonalizationAccent`); the ring and the file meters paint with it when it reads on
	 * the card (kitecorp/transferify-ui#2888).
	 */
	accentColor: string | null;
	/** Whether the upload's requests are going through, being retried, or waiting for a network (ui#3204). */
	connection: ConnectionState;
	/** This browser keeps the upload, so a closed tab can be continued here (ui#3204). */
	continuable: boolean;
}

interface MeterProps {
	percent: number;
	color: string;
	height: number;
	marginTop: number;
	label: string;
}

/** The narrow per-file meter; the large overall meter is the semantic SVG ring below. */
function Meter({percent, color, height, marginTop, label}: MeterProps) {
	return (
		<MeterTrack
			$height={height}
			$marginTop={marginTop}
			role="progressbar"
			aria-label={label}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(percent)}
		>
			<MeterFill $color={color} style={{width: `${percent}%`}}/>
		</MeterTrack>
	);
}

interface GaugeArcProps {
	percent: number;
	color: string;
	label: string;
	valueText: string;
}

/** The semantic ring and its eased arc share one percentage target. */
function GaugeArc({percent, color, label, valueText}: GaugeArcProps) {
	const eased = useEasedValue(percent, EASED_VALUE_SECONDS);
	const pathLength = useTransform(eased, value => value / 100);

	return (
		<GaugeSvg
			role="progressbar"
			aria-label={label}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(percent)}
			aria-valuetext={valueText}
		>
			<GaugeTrack/>
			<GaugeProgressArc $color={color} style={{pathLength}}/>
		</GaugeSvg>
	);
}

interface RateProps {
	readout: ProgressReadoutValues;
	locale: Locale;
	words: EtaWords;
	/** From {@link uploadSpeedDebugEnabled} — the speed prefix is an internal debug readout (ui#1840). */
	showSpeed: boolean;
}

/**
 * "{eta} left" (with a debug-only "{speed} · " prefix) — the speed steps with the EMA publication
 * while the whole-number ETA travels between adjacent countdown targets. Interpolating the speed
 * itself would rewrite its one decimal many times per second, recreating the flicker this ticket
 * removes.
 */
function RateReadout({readout, locale, words, showSpeed}: RateProps) {
	const eta = useEasedValue(readout.etaSeconds);
	const line = useTransform(eta,
		(travellingEta: number) => formatRate(readout.bytesPerSecond, travellingEta, locale, words, showSpeed));

	return <GaugeDetail data-testid="upload-rate-readout" aria-hidden="true">{line}</GaugeDetail>;
}

function UploadProgressView(
	{files, progress, readout, onCancel, game, completedWhilePlaying, onContinue, accentColor, connection, continuable}:
		Omit<Props, "mode">,
) {
	const {tokens} = useTheme();
	const arcColor = progressArcColor(accentColor, tokens.progressFill, tokens.card);
	const {sender, games} = useMessages();
	const copy = sender.progress;
	const {locale} = useLocale();
	const titleId = useId();
	const showSpeed = uploadSpeedDebugEnabled();
	// Which sentence is `uploadNoteKey`'s rule; this only looks the words up.
	const notes = {
		keepOpen: copy.keepOpenWhileUploading,
		keepOpenContinuable: `${copy.keepOpenWhileUploading} ${copy.continuableHere}`,
		reconnecting: copy.reconnectingNote,
		offline: copy.offlineNote,
	};
	const note = notes[uploadNoteKey(connection, continuable)];
	const spokenNoteKey = announcedNoteKey(connection);
	const etaWords = useMemo<EtaWords>(() => ({timeLeft: copy.timeLeft, days: copy.etaDays}), [copy]);
	// Before the model's first window closes there is nothing sampled yet, and the card shows ZERO
	// rather than the live raw progress. Falling back to raw looks better for that one second and then
	// steps BACKWARDS: the first publication commits the sample taken when the gate fired, which any
	// progress event landing before React committed has already moved past. The genuine local-stack
	// journey caught exactly that — two readings 3 ms apart with the percentage going down.
	const pending = useMemo(
		() => ({bytesSent: 0, totalBytes: progress.totalBytes, perFileBytes: []}),
		[progress.totalBytes]);
	// ONE sampled reading behind every number on the card, rows included.
	const displayed = readout?.sample ?? pending;
	const displayedPercent = percentOf(displayed.bytesSent, displayed.totalBytes);
	const percentText = formatPercent(displayedPercent, locale);
	const uploadedText = formatBytes(displayed.bytesSent, locale, 1);
	const totalText = formatBytes(displayed.totalBytes, locale, 1);
	const rateText = readout === null
		? null
		: formatRate(readout.bytesPerSecond, readout.etaSeconds, locale, etaWords, showSpeed);
	// A held upload is complete, whatever the last sample said: the spoken gauge says so too.
	const spokenOverall = completedWhilePlaying
		? copy.gaugeValueText(formatPercent(100, locale), totalText, totalText, null)
		: copy.gaugeValueText(percentText, uploadedText, totalText, rateText);
	// Deliberately stale between steps: the dependency is the STEP, so the sentence is captured when
	// the sampled gauge crosses it and then holds. Animated visible text stays aria-hidden, preventing
	// the polite region from turning a smooth transition into an announcement storm.
	const step = announcementStep(displayedPercent);
	const announcement = useMemo(() => spokenNoteKey !== null ? notes[spokenNoteKey]
		: step === 0 ? "" : spokenOverall, [step, spokenNoteKey]);

	const gameProgress = uploadGameProgress(displayed.bytesSent, displayed.totalBytes, completedWhilePlaying);
	// This card re-renders on every progress report (ten a second), so at 10,000 files only the rows
	// near the strip's view are drawn (kitecorp/transferify-ui#2942). One list for both layouts below:
	// only one of them is ever on screen.
	const fileList = useWindowedList<HTMLDivElement>({
		count: files.length,
		estimateRowHeight: QUIET_LIST_ROW_HEIGHT_ESTIMATE_PX,
		gap: QUIET_LIST_ROW_GAP_PX,
	});

	const fileRows = (flush: boolean) => (
		<QuietList $flush={flush} ref={fileList.listRef} onFocus={fileList.onFocus} onBlur={fileList.onBlur}>
			{fileList.rows.map(({index, props}) => {
				const file = files[index];
				const kind = senderFileKind(file.name);
				const filePercent = percentOf(displayed.perFileBytes[index] ?? 0, file.size);
				return (
					<FileRow key={`${file.name}-${index}`} {...props}>
						<ThumbSm data-testid="file-kind-badge" $tone={kind.tone}>{kind.label}</ThumbSm>
						<FileInfo>
							<FileTop>
								<FileName>{file.name}</FileName>
								<FilePercent data-testid="file-percent">
									<AnimatedNumber
										value={filePercent}
										durationSeconds={EASED_METER_SECONDS}
										format={value => formatPercent(value, locale)}
									/>
								</FilePercent>
							</FileTop>
							<Meter percent={filePercent} color={arcColor} height={4} marginTop={4} label={copy.fileAria(file.name)}/>
						</FileInfo>
					</FileRow>
				);
			})}
		</QuietList>
	);

	return (
		<UploadRoot data-testid="upload-gauge-card" role="region" aria-labelledby={titleId}>
			<VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
				{announcement}
			</VisuallyHidden>
			<Head>
				<Title id={titleId}>
					{completedWhilePlaying ? games.common.done.upload
						: connection === "connected" ? copy.uploading : copy.reconnecting}
				</Title>
				{!completedWhilePlaying && <CancelButton type="button" onClick={onCancel}>{copy.cancel}</CancelButton>}
			</Head>
			{!completedWhilePlaying && (
				<UploadNote data-testid="upload-note">
					{note}
				</UploadNote>
			)}
			{game.open && game.game !== null ? (
				<Stage>
					<StageContent>
						<CompactGauge
							role="progressbar"
							aria-label={copy.overallAria}
							aria-valuemin={0}
							aria-valuemax={100}
							aria-valuenow={Math.round(completedWhilePlaying ? 100 : displayedPercent)}
							aria-valuetext={spokenOverall}
						>
							<CompactPercent aria-hidden="true">
								{completedWhilePlaying ? formatPercent(100, locale) : percentText}
							</CompactPercent>
							<CompactMeta aria-hidden="true">
								{copy.sentOfTotal(completedWhilePlaying ? totalText : uploadedText, totalText)}
								<br/>
								{completedWhilePlaying ? games.common.done.upload : rateText}
							</CompactMeta>
						</CompactGauge>
						<GameStage
							game={game.game}
							mode="upload"
							progress={gameProgress}
							endless={false}
							onClose={completedWhilePlaying ? onContinue : game.close}
							onContinue={onContinue}
							onRunFinished={game.runFinished}
							onRunClosed={game.runClosed}
						/>
						{fileRows(false)}
					</StageContent>
				</Stage>
			) : (
				<>
					<Gauge>
						<GaugeVisual>
							<GaugeArc percent={displayedPercent} color={arcColor} label={copy.overallAria} valueText={spokenOverall}/>
							<GaugeCenter aria-hidden="true">
								<GaugePercent data-testid="upload-gauge-percent">
									<AnimatedNumber
										value={displayedPercent}
										durationSeconds={EASED_VALUE_SECONDS}
										format={value => formatPercent(value, locale)}
									/>
								</GaugePercent>
								{readout && <RateReadout readout={readout} locale={locale}
									words={etaWords} showSpeed={showSpeed}/>}
								<GaugeDetail data-testid="upload-gauge-bytes">
									<AnimatedNumber
										value={displayed.bytesSent}
										durationSeconds={EASED_VALUE_SECONDS}
										// ROUNDED: below the KB rung `formatBytes` renders the number as-is, so a
										// travelling value would paint "413.7719726562 Bytes" on a small transfer.
										format={value => copy.sentOfTotal(formatBytes(Math.round(value), locale, 1), totalText)}
									/>
								</GaugeDetail>
							</GaugeCenter>
						</GaugeVisual>
						{game.offersPlay && game.game !== null && (
							<GameOfferSlot>
								<GameOffer game={game.game} onPlay={game.play} focusOnMount={game.returning}/>
							</GameOfferSlot>
						)}
					</Gauge>
					{/* The files keep the framed box; the ring sits on the card above it (owner decision
					    2026-09-25, kitecorp/transferify-ui#2888: "ring should be outside the files background"). */}
					<Stage $filesOnly>
						<StageContent>{fileRows(true)}</StageContent>
					</Stage>
				</>
			)}
		</UploadRoot>
	);
}

export function ProgressCard(
	{mode, files, progress, readout, onCancel, game, completedWhilePlaying, onContinue, accentColor, connection, continuable}:
		Props,
) {
	const {sender} = useMessages();
	const {tokens} = useTheme();
	if (mode === "upload") {
		return (
			<UploadProgressView
				files={files}
				progress={progress}
				readout={readout}
				onCancel={onCancel}
				game={game}
				completedWhilePlaying={completedWhilePlaying}
				onContinue={onContinue}
				accentColor={accentColor}
				connection={connection}
				continuable={continuable}
			/>
		);
	}
	return (
		<div role="status" aria-live="polite">
			<InstantTitle>{sender.progress.instantTitle}</InstantTitle>
			<InstantSub>{sender.filesCount(files.length)} · {sender.progress.keepTabOpen}</InstantSub>
			<MeterTrack $height={10} $marginTop={16}><IndeterminateFill $color={progressArcColor(accentColor, tokens.progressFill, tokens.card)}/></MeterTrack>
		</div>
	);
}
